import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Loader2, X, ShieldCheck, Save, Plus, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const MODULE_LABELS = {
  attendance: 'Presenze',
  company: 'Azienda',
  department: 'Reparti',
  ferie: 'Ferie',
  location: 'Locali',
  orders: 'Ordini',
  personale: 'Personale',
  security: 'Utenti e Accessi',
  turni: 'Turni',
  warehouse: 'Magazzino',
};

const moduleLabel = (module) => MODULE_LABELS[module] || module;

/**
 * SecurityEditorModal — U2 role/scope/capability editor.
 * Loads /api/security/catalog + /api/security/users/{id} and lets an authorized
 * actor edit role_scopes and capability_overrides. All ceiling checks are done
 * server-side (this UI only shows options the actor can actually delegate).
 */
export default function SecurityEditorModal({ user_id, personName, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [detail, setDetail] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [locationsMap, setLocationsMap] = useState({}); // company_id -> locations
  const [departmentsMap, setDepartmentsMap] = useState({}); // location_id -> departments
  const [busy, setBusy] = useState(false);

  const [scopes, setScopes] = useState([]);
  const [overrides, setOverrides] = useState({}); // cap_code -> mode
  const [openModule, setOpenModule] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cat, det, comps] = await Promise.all([
          apiFetch('/api/security/catalog'),
          apiFetch(`/api/security/users/${user_id}`),
          apiFetch('/api/companies'),
        ]);
        setCatalog(cat);
        setDetail(det);
        setCompanies(comps);
        setScopes(det.role_scopes.map((rs) => ({
          role_code: rs.role_code, company_id: rs.company_id || '',
          location_id: rs.location_id || '', department_id: rs.department_id || '',
        })));
        const om = {};
        det.capability_overrides.forEach((o) => { om[o.capability_code] = o.mode; });
        setOverrides(om);
      } catch (e) { toast.error(e.message); onClose(); }
    })();
  }, [user_id]);  // eslint-disable-line

  const loadLocations = async (cid) => {
    if (!cid || locationsMap[cid]) return;
    try {
      const ls = await apiFetch(`/api/locations?company_id=${cid}`);
      setLocationsMap((m) => ({ ...m, [cid]: ls }));
    } catch (_) { /* ignore */ }
  };

  const loadDepartments = async (lid) => {
    if (!lid || departmentsMap[lid]) return;
    try {
      const ds = await apiFetch(`/api/departments?location_id=${lid}`);
      setDepartmentsMap((m) => ({ ...m, [lid]: ds }));
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    scopes.forEach((s) => {
      if (s.company_id) loadLocations(s.company_id);
      if (s.location_id) loadDepartments(s.location_id);
    });
  }, [scopes]);  // eslint-disable-line

  const isDelegable = (cap) => {
    if (!catalog) return false;
    if (catalog.actor_is_global) return true;
    const set = new Set(catalog.actor_effective_capabilities);
    if (set.has(cap)) return true;
    const family = cap.split('.')[0] + '.*';
    return set.has(family);
  };

  const roleEffective = useMemo(() => new Set(detail?.effective_capabilities || []), [detail]);

  const capsByModule = useMemo(() => {
    const g = {};
    (catalog?.capabilities || []).forEach((c) => {
      g[c.module] = g[c.module] || [];
      g[c.module].push(c);
    });
    return g;
  }, [catalog]);

  const submit = async () => {
    if (scopes.length === 0) { toast.error('Aggiungi almeno un ruolo/scope'); return; }
    setBusy(true);
    try {
      await apiFetch(`/api/security/users/${user_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          role_scopes: scopes.map((s) => ({
            role_code: s.role_code,
            company_id: s.company_id || null,
            location_id: s.location_id || null,
            department_id: s.department_id || null,
          })),
          capability_overrides: Object.entries(overrides).map(([code, mode]) => ({ capability_code: code, mode })),
        }),
      });
      toast.success('Permessi aggiornati');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addScope = () => setScopes((xs) => [...xs, { role_code: 'DIPENDENTE', company_id: '', location_id: '', department_id: '' }]);
  const removeScope = (i) => setScopes((xs) => xs.filter((_, k) => k !== i));
  const patchScope = (i, p) => setScopes((xs) => xs.map((s, k) => k === i ? { ...s, ...p } : s));

  const setOv = (code, mode) => setOverrides((o) => {
    const n = { ...o };
    if (mode === null) delete n[code]; else n[code] = mode;
    return n;
  });

  if (!catalog || !detail) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="security-editor-title">
        <div className="rounded-2xl border border-app-border bg-app-surface p-6 flex items-center gap-2 text-stone-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" data-testid="security-editor-modal" role="dialog" aria-modal="true" aria-labelledby="security-editor-title" onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className="flex h-[100dvh] max-h-[100dvh] w-full max-w-2xl flex-col overflow-hidden rounded-none border border-app-border bg-app-surface sm:h-auto sm:max-h-[92vh] sm:rounded-2xl" data-testid="security-editor-panel" onClick={(e) => e.stopPropagation()}>
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-stone-100 bg-app-surface px-4 pb-3 pt-4 sm:px-6 sm:pt-5">
          <div>
            <div id="security-editor-title" className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Ruolo · Scope · Permessi</div>
            <div className="text-lg font-semibold text-brand-navy mt-0.5">{personName || detail.email}</div>
          </div>
          <button onClick={onClose} aria-label="Chiudi modifica permessi" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-400 hover:bg-stone-50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"><X className="w-5 h-5" /></button>
        </header>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-5"
          data-testid="security-editor-content"
          style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >

        {/* SCOPE / ROLES */}
        <section className="mb-6">
          <h3 className="text-sm font-bold text-brand-navy mb-2">Dove può operare (Ruolo + Scope)</h3>
          <div className="space-y-2">
            {scopes.map((s, i) => (
              <ScopeRow key={i} idx={i} scope={s} catalog={catalog} companies={companies}
                        locations={locationsMap[s.company_id] || []}
                         departments={departmentsMap[s.location_id] || []}
                        onChange={(p) => patchScope(i, p)} onRemove={() => removeScope(i)} />
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={addScope} className="mt-2 gap-1" data-testid="btn-add-scope">
            <Plus className="w-3.5 h-3.5" /> Aggiungi ruolo/scope
          </Button>
        </section>

        {/* CAPABILITIES */}
        <section data-testid="capabilities-section">
          <h3 className="text-sm font-bold text-brand-navy mb-2">Cosa può fare (Capability)</h3>
          <p className="text-xs text-stone-500 mb-3">
            Le capability di default vengono dal ruolo. Puoi <b>aggiungere</b> (GRANT) o <b>togliere</b> (DENY) singoli permessi per personalizzazione.
          </p>
          {Object.entries(capsByModule).map(([mod, caps]) => (
            <div key={mod} className="mb-2 overflow-hidden rounded-xl border border-stone-200" data-testid={`module-${mod}`}>
              <button
                type="button"
                className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2.5 text-left font-semibold text-sm text-brand-navy transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
                aria-expanded={openModule === mod}
                aria-controls={`module-content-${mod}`}
                onClick={() => setOpenModule((current) => (current === mod ? null : mod))}
                data-testid={`module-toggle-${mod}`}
              >
                <span>{moduleLabel(mod)}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-stone-400 transition-transform ${openModule === mod ? 'rotate-180 text-brand-green' : ''}`} aria-hidden="true" />
              </button>
              {openModule === mod && (
                <div id={`module-content-${mod}`} className="space-y-1 border-t border-stone-100 p-3 pt-2" data-testid={`module-content-${mod}`}>
                  {caps.map((c) => {
                    const inheritedFromRole = roleEffective.has(c.code);
                    const mode = overrides[c.code];
                    const delegable = isDelegable(c.code);
                    return (
                      <div key={c.code} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-1 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="break-words text-brand-navy">{c.label}</div>
                          <div className="break-all text-[10px] text-stone-400 font-mono">{c.code}</div>
                        </div>
                        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
                          {inheritedFromRole && !mode && (
                            <span className="hidden text-[10px] px-1.5 py-0.5 rounded bg-brand-green/10 text-brand-green sm:inline">Da ruolo</span>
                          )}
                          {mode === 'GRANT' && <span className="hidden text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 sm:inline">Personalizzato</span>}
                          {mode === 'DENY' && <span className="hidden text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 sm:inline">Bloccato</span>}
                          <select
                            disabled={!delegable}
                            value={mode || (inheritedFromRole ? 'ROLE' : 'NONE')}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'ROLE' || v === 'NONE') setOv(c.code, null);
                              else setOv(c.code, v);
                            }}
                            data-testid={`cap-${c.code}`}
                            className="h-9 min-w-[8.25rem] rounded border border-stone-300 bg-white px-1.5 text-xs disabled:bg-stone-50 disabled:text-stone-400"
                          >
                            {inheritedFromRole
                              ? <option value="ROLE">Attiva (da ruolo)</option>
                              : <option value="NONE">Disattiva</option>}
                            <option value="GRANT">Attiva (personalizzata)</option>
                            <option value="DENY">Disattiva forzata</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </section>

        </div>

        <footer
          className="flex shrink-0 justify-end gap-2 border-t border-stone-100 bg-app-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-6"
          data-testid="security-editor-footer"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <Button variant="outline" onClick={onClose} data-testid="btn-cancel-security">Annulla</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand-green hover:bg-brand-green-hover text-white gap-2" data-testid="btn-save-security">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
          </Button>
        </footer>
      </div>
    </div>
  );
}

function ScopeRow({ idx, scope, catalog, companies, locations, departments, onChange, onRemove }) {
  // TITOLARE is a standalone tenant account, not an employee role. It is
  // managed only from the CEO tenant-access flow.
  const assignableRoles = (catalog.roles || []).filter((r) => r.assignable && !['CEO', 'TITOLARE'].includes(r.code));
  return (
    <div className="rounded-xl border border-stone-200 p-3 space-y-2" data-testid={`scope-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-stone-500">Ambito #{idx + 1}</span>
        <button onClick={onRemove} aria-label={`Rimuovi ambito ${idx + 1}`} className="inline-flex h-10 w-10 items-center justify-center rounded-full text-red-600 hover:bg-red-50 hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200" data-testid={`btn-remove-scope-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="text-xs text-stone-500">Ruolo</label>
          <select value={scope.role_code} onChange={(e) => onChange({ role_code: e.target.value })}
                  data-testid={`scope-role-${idx}`}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm">
            {assignableRoles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-stone-500">Azienda</label>
          <select value={scope.company_id} onChange={(e) => onChange({ company_id: e.target.value, location_id: '', department_id: '' })}
                  data-testid={`scope-company-${idx}`}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm">
            <option value="">— tutte —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-stone-500">Locale</label>
          <select value={scope.location_id} onChange={(e) => onChange({ location_id: e.target.value })}
                  disabled={!scope.company_id}
                  data-testid={`scope-location-${idx}`}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm disabled:bg-stone-50">
            <option value="">— tutti i locali dell'azienda —</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-stone-500">Reparto</label>
          <select value={scope.department_id} onChange={(e) => onChange({ department_id: e.target.value })}
                  disabled={!scope.location_id}
                  data-testid={`scope-department-${idx}`}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm disabled:bg-stone-50">
            <option value="">— tutti i reparti del locale —</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
